import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '../config';
import { error as logError } from '../lib/logger';

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Feb 2026 SEC-009 — cookie-only auth.
// The backend sets httpOnly `access_token` / `refresh_token` cookies on
// login, register, refresh and admin/impersonate (see `get_current_user`
// in server.py). We removed the localStorage token cache that also
// attached an `Authorization: Bearer …` header — those tokens were
// XSS-readable and duplicated what the httpOnly cookies already carry.
//
// Consequence: every request that needs an authenticated session MUST
// travel with credentials. Setting the default here means individual
// axios.* callsites don't have to remember `withCredentials: true`.
axios.defaults.withCredentials = true;

function setupAxiosInterceptors() {
  // Response: auto-refresh once on 401. The refresh cookie is httpOnly,
  // so we hit /auth/refresh with an empty body — the browser attaches
  // the refresh cookie automatically. If refresh itself fails, boot the
  // user back to the login page.
  let isRefreshing = false;
  let failedQueue = [];
  const processQueue = () => {
    failedQueue.forEach(({ resolve }) => resolve());
    failedQueue = [];
  };
  const rejectQueue = (err) => {
    failedQueue.forEach(({ reject }) => reject(err));
    failedQueue = [];
  };

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/')
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => axios(originalRequest));
        }
        originalRequest._retry = true;
        isRefreshing = true;
        try {
          await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
          processQueue();
          return axios(originalRequest);
        } catch (refreshError) {
          rejectQueue(refreshError);
          // Only bounce to /login if we're inside the app, not already on
          // a public page (avoids fighting with a fresh visitor on /login).
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );
}
setupAxiosInterceptors();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      // /auth/me is inside the interceptor's `/auth/` skip-list, so we
      // don't get a refresh attempt here — a 401 just means "no valid
      // session cookie". Nothing to clear on the client (cookies are
      // httpOnly); just flip user state.
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Backend sets httpOnly access + refresh cookies on this response.
      // The tokens still ship in `data.*` for non-browser clients, but
      // the browser session runs off cookies alone from here on.
      setUser(data);
      return { success: true };
    } catch (e) {
      // Surface the real reason so users can report what's failing on production
      const serverDetail = e.response?.data?.detail;
      if (serverDetail) {
        return { success: false, error: formatApiErrorDetail(serverDetail) };
      }
      const status = e.response?.status;
      // 520 = Cloudflare "origin returned unknown error" — almost always a
      // stale cached frontend bundle or mismatched cookies from a previous
      // deploy. Tell the user exactly how to recover instead of a generic code.
      if (status === 520 || status === 521 || status === 522 || status === 523) {
        return {
          success: false,
          error: 'The server hit a routing hiccup (HTTP ' + status + '). Please hard-refresh this page (Cmd+Shift+R / Ctrl+F5) or open it in an incognito window and try again.',
        };
      }
      if (status) {
        return { success: false, error: `Login failed (HTTP ${status}). Please try again.` };
      }
      // Network error (no response received) — most common on production domain misconfigurations
      const target = `${BACKEND_URL || window.location.origin}/api/auth/login`;
      return {
        success: false,
        error: `Cannot reach the server at ${target}. ${e.message || 'Check your connection.'}`,
      };
    }
  }, []);

  const register = useCallback(async (email, password, name, storeName) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name, store_name: storeName },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      logError('Logout request failed:', err);
    } finally {
      // Server has cleared the httpOnly cookies (delete_cookie in /auth/logout).
      // Nothing to purge on the client — just flip user state.
      setUser(false);
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
      return true;
    } catch {
      setUser(false);
      return false;
    }
  }, [checkAuth]);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshToken, checkAuth }),
    [user, loading, login, register, logout, refreshToken, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
